// ═══════════════════════════════════════════════════════════════════════════════
// Error Boundary Component
// Catches React errors and displays fallback UI
// ═══════════════════════════════════════════════════════════════════════════════

import { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Unhandled render error', {
      componentStack: errorInfo.componentStack,
    });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <Card
            className="border-destructive/50 bg-destructive/5 p-6"
            role="alert"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle
                className="h-6 w-6 shrink-0 text-destructive"
                aria-hidden="true"
              />
              <div className="flex-1">
                <h3 className="font-semibold text-destructive">Something went wrong</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Tacit could not display this section. Try again. If the problem
                  continues, reload the page or contact support.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 min-h-11"
                  onClick={this.handleRetry}
                >
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                  Try again
                </Button>
              </div>
            </div>
          </Card>
        )
      );
    }

    return this.props.children;
  }
}
